import { Col, Input, Label, Row } from "reactstrap";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getToolOptions } from "features/Tools/services/ToolService";
import { getUserOptionsWithEmployeeProfile } from "features/Users/services/UserService";
import {
  EmployeeOption,
  EmployeeSingleValue,
} from "features/Employees/components/EmployeeSelectComponents";
import {
  ToolInOutSourceType,
  ToolInOutSourceTypeLabel,
} from "features/ToolInOuts/constants/tool-in-out.constants";

export type ToolInOutListFiltersState = {
  outstandingOnly: boolean;
  userId: number | "";
  sourceType: ToolInOutSourceType | "";
  toolId: number | "";
};

export function emptyToolInOutListFilters(): ToolInOutListFiltersState {
  return {
    outstandingOnly: false,
    userId: "",
    sourceType: "",
    toolId: "",
  };
}

export function countToolInOutListFiltersActive(
  f: ToolInOutListFiltersState,
): number {
  let n = 0;
  if (f.outstandingOnly) n += 1;
  if (f.userId !== "") n += 1;
  if (f.sourceType) n += 1;
  if (f.toolId !== "") n += 1;
  return n;
}

export type ToolInOutListFiltersProps = {
  filters: ToolInOutListFiltersState;
  onChange: (next: ToolInOutListFiltersState) => void;
};

export default function ToolInOutListFilters({
  filters,
  onChange,
}: ToolInOutListFiltersProps) {
  const patch = (partial: Partial<ToolInOutListFiltersState>) =>
    onChange({ ...filters, ...partial });

  return (
    <Row className="g-2 g-md-3 align-items-end flex-grow-1 w-100 mb-0">
      <Col xs={12} sm={6} md={3}>
        <Label className="form-label text-muted small mb-1">
          Source type
        </Label>
        <Input
          type="select"
          className="form-select"
          value={filters.sourceType}
          onChange={(e) =>
            patch({
              sourceType: (e.target.value || "") as ToolInOutSourceType | "",
            })
          }
        >
          <option value="">All sources</option>
          {(Object.values(ToolInOutSourceType) as ToolInOutSourceType[]).map(
            (t) => (
              <option key={t} value={t}>
                {ToolInOutSourceTypeLabel[t]}
              </option>
            ),
          )}
        </Input>
      </Col>

      <Col xs={12} sm={6} md={3}>
        <Label className="form-label text-muted small mb-1">Tool</Label>
        <AsyncPaginatedSelect
          fetchOptions={getToolOptions}
          placeholder="All tools…"
          value={filters.toolId === "" ? "" : filters.toolId}
          onChange={(opt: any) => {
            const val = Array.isArray(opt) ? opt[0] : opt;
            patch({ toolId: val?.value ?? "" });
          }}
        />
      </Col>

      <Col xs={12} sm={6} md={3}>
        <Label className="form-label text-muted small mb-1">
          Concerned person
        </Label>
        <AsyncPaginatedSelect
          fetchOptions={getUserOptionsWithEmployeeProfile}
          components={{
            Option: EmployeeOption,
            SingleValue: EmployeeSingleValue,
          }}
          placeholder="All users…"
          value={filters.userId}
          onChange={(opt: any) => {
            const val = Array.isArray(opt) ? opt[0] : opt;
            patch({ userId: val?.value != null ? Number(val.value) : "" });
          }}
        />
      </Col>

      <Col xs={12} sm={6} md={3}>
        <div className="form-check form-switch mb-0 pb-2">
          <Input
            type="checkbox"
            className="form-check-input"
            id="tool-in-out-outstanding-only"
            checked={filters.outstandingOnly}
            onChange={(e) => patch({ outstandingOnly: e.target.checked })}
          />
          <Label
            className="form-check-label"
            htmlFor="tool-in-out-outstanding-only"
          >
            Outstanding only
          </Label>
        </div>
      </Col>
    </Row>
  );
}
